import React from 'react';

import type { AttainmentDrillNode, AttainmentPeriodMeta } from '../../../hooks/attainment-hooks';
import {
  attainmentPct,
  formatUsdCompact,
} from '../../../support/enablementLeader';
import StbAccordion from '../../security-toolbox-ui/StbAccordion';
import CampaignChartCard, { ChartGrid, KpiCards } from '../Campaigns/CampaignChartCard';
import { findPeriodByLabel, quotaBookingsRows } from '../../../support/attainmentLeader';
import AttainmentElementsSection from './AttainmentElementsSection';
import AttainmentNoData from './AttainmentNoData';

/** Leader > Attainment > Individual. One person's attainment for the selected goaling interval only. */

interface Props {
  node: AttainmentDrillNode;
  /** The selected goaling interval (e.g. H1 FY27); only this period's figures are shown. */
  interval: AttainmentPeriodMeta | null;
}

const HBAR_SETTINGS = {
  valueFormat: 'number',
  axisLeftLegendOffset: -96,
  marginLeft: 112,
  maxLeftMarginRatio: 0.5,
};

export default function AttainmentIndividualContributorPage({ node, interval }: Props) {
  const owner = node.email.toLowerCase();
  // The person's own detail for that interval. When they have none (no plan then) the KPIs read "—".
  const period = findPeriodByLabel(node.own?.periods, interval?.label);
  const stats = period?.total ?? null;
  const pct = attainmentPct(stats);
  const quotaChart = quotaBookingsRows(stats);
  const intervalLabel = interval?.label || interval?.interval || '';
  const key = `${interval?.sheet_id ?? intervalLabel ?? 'current'}`;

  // No plan in the selected interval -> show nothing but a note (never another interval's figures).
  if (!period) return <AttainmentNoData intervalLabel={intervalLabel} who={node.email} />;

  return (
    <div className="flex flex-col gap-4 w-full min-w-0">
      <StbAccordion
        variant="minimal"
        label={intervalLabel ? `Attainment — ${intervalLabel}` : 'Attainment'}
        isOpen={true}
        toggleIsOpen={() => {}}
      >
        <div style={{ padding: 12 }}>
          <KpiCards
            kpis={[
              { label: 'Attainment', value: pct == null ? '—' : `${pct}%` },
              { label: 'Bookings', value: formatUsdCompact(stats?.bookings) },
              { label: 'Quota', value: formatUsdCompact(stats?.quota) },
            ]}
          />
          <ChartGrid>
            <CampaignChartCard
              title="Quota vs bookings"
              subtitle={`${intervalLabel || 'Interval'} (USD, ${quotaChart.scale.unit})`}
              chartType="horizontal_bar_chart"
              data={quotaChart.rows}
              tileKey={`att-ic-${owner}-${key}-quota-${quotaChart.scale.unit}`}
              tooltipKeys={{ label: 'Measure', value: `USD (${quotaChart.scale.unit})` }}
              settings={{ ...HBAR_SETTINGS, axisBottomLegend: `USD (${quotaChart.scale.unit})` }}
              empty="No quota recorded."
            />
          </ChartGrid>
        </div>
      </StbAccordion>

      <StbAccordion
        variant="minimal"
        label={intervalLabel ? `Plan Elements — ${intervalLabel}` : 'Plan Elements'}
        isOpen={true}
        toggleIsOpen={() => {}}
      >
        <div style={{ padding: 12 }}>
          <AttainmentElementsSection
            elements={period.elements ?? []}
            keyPrefix={`att-ic-${owner}-${key}`}
            intervalLabel={intervalLabel}
          />
        </div>
      </StbAccordion>
    </div>
  );
}
